import { Component, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { NgScrollbarModule } from "ngx-scrollbar";
import { MatExpansionModule } from "@angular/material/expansion";
import {
  RouterLink,
  RouterLinkActive,
  RouterModule,
  Router,
} from "@angular/router";
import { ToggleService } from "./toggle.service";
import { CommonModule, NgClass } from "@angular/common";
import { CustomizerSettingsService } from "src/app/core/services/customizer-settings.service";
import { AuthService } from "src/app/core/services/auth.service";
import { environment } from "src/environments/environment";

interface MenuItem {
  title: string;
  icon?: string;
  route?: string;
  queryParams?: Record<string, any>;
  children?: MenuItem[];
  visibility?: "DCO" | "JURY" | "ALL";
  restrictedRoles?: string[];
  /** Allowlist. When set, the entry is shown only for these roles. */
  allowedRoles?: string[];
  /** Shown in local and development builds only. Production builds omit it. */
  devOnly?: boolean;
}

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [
    NgScrollbarModule,
    MatExpansionModule,
    RouterLinkActive,
    RouterModule,
    RouterLink,
    NgClass,
    CommonModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
})
export class SidebarComponent {
  // isSidebarToggled
  isSidebarToggled = false;

  loggedInUser = localStorage.getItem("loggedInUser")
    ? JSON.parse(localStorage.getItem("loggedInUser")!)
    : null;

  menuItems: MenuItem[] = [
    {
      title: "Dashboard",
      icon: "dashboard",
      route: "/department/dashboard",
      restrictedRoles: ["SUPPORT"],
    },
    {
      title: "Event Request",
      icon: "event",
      devOnly: true,
      children: [
        {
          title: "Overall Request List",
          route: "/department/event-request",
          visibility: "ALL",
        },
        {
          title: "Pending Request List",
          route: "/department/pending-requests",
        },
        {
          title: "Work Order List",
          route: "/department/workorder-list",
          visibility: "ALL",
        },
      ],
    },
    {
      title: "Registration",
      icon: "app_registration",
      restrictedRoles: ["JURY"],
      children: [
        {
          title: "Cultural Troupe",
          route: "/department/registration/ct",
        },
        {
          title: "District CT Summary",
          route: "/department/registration/district-summary",
          restrictedRoles: ["JURY", "TROUPE", "AGENCY", "ARTIST"],
        },
        {
          title: "Incomplete Troupe",
          route: "/department/registration/incomplete",
        },
        {
          title: "Deactivated Troupes",
          route: "/department/registration/deactivated",
        },
        {
          title: "Requesting Agency",
          route: "/department/registration/ra",
        },
        {
          title: "Members",
          route: "/department/registration/members",
        },
      ],
    },
    {
      title: "Audition",
      icon: "theater_comedy",
      restrictedRoles: ["JURY"],
      children: [
        {
          title: "Audition Notifications",
          route: "/department/registration/auditions",
        },
        {
          title: "Audition Attendance",
          route: "/department/registration/audition-attendance",
        },
        {
          title: "Absent Troupes",
          route: "/department/registration/absentee",
        },
      ],
    },
    {
      title: "Notifications",
      icon: "notifications",
      route: "/department/notifications",
      restrictedRoles: ["DCO", "ASO", "DIRECTOR", "DYDIRECTOR", "ACCOUNTS", "ADMIN", "JURY"]
    },
    // Dev-only: these groups stay out of production builds. Role rules below
    // still apply when the menu is visible.
    {
      title: "Report",
      icon: "assessment",
      devOnly: true,
      children: [
        {
          title: "Category-Wise Payment Report",
          route: "/department/report/category-wise-payment",
        },
        {
          title: "District-Wise Count Report",
          route: "/department/report/district-wise-count",
        },
        {
          title: "Audit List Report",
          route: "/department/report/audit-list",
        },
        {
          title: "Event Type Report",
          route: "/department/report/event-type",
        },
        {
          title: "Event Category Report",
          route: "/department/report/event-category",
        },
        {
          title: "Venue Utilization Report",
          route: "/department/report/venue-utilization",
        },
        {
          title: "Artist Performance Report",
          route: "/department/report/artist-performance",
        },
        {
          title: "Audition Pipeline Report",
          route: "/department/report/audition-pipeline",
        },
        {
          title: "Troupe Booking Report",
          route: "/department/report/troupe-booking",
        },
        {
          title: "Feedback Summary Report",
          route: "/department/report/feedback-summary",
        },
        {
          title: "Monthly Trend Report",
          route: "/department/report/monthly-trend",
        },
        {
          title: "Agency Revenue Report",
          route: "/department/report/agency-revenue",
        },
        {
          title: "User Activity Report",
          route: "/department/report/user-activity",
        },
        {
          title: "System Health Report",
          route: "/department/report/system-health",
        },
        {
          title: "Data Export Report",
          route: "/department/report/data-export",
        },
      ],
    },
    {
      title: "Payments",
      icon: "payment",
      restrictedRoles: ["SUPPORT"],
      devOnly: true,
      children: [
        {
          title: "Overall Payments",
          route: "/department/payment/overall-event-payments",
          visibility: "ALL",
        },
        {
          title: "Approved Payments",
          route: "/department/payment/approved-event-payments",
          visibility: "ALL",
        },
        {
          title: "Rejected Payments",
          route: "/department/payment/rejected-event-payments",
          visibility: "ALL",
        },
      ],
    },
    {
      title: "Accounts",
      icon: "account_balance_wallet",
      route: "/department/accounts",
      restrictedRoles: ["JURY", "ADMIN", "DCO"],
    },
    {
      title: "Troupe Bank Details",
      icon: "account_balance",
      route: "/department/troupe-bank-details",
      devOnly: true,
    },
    {
      title: "Help & Support",
      icon: "help",
      route: "/department/support",
      restrictedRoles: ["DCO", "ASO", "DYDIRECTOR", "DIRECTOR"],
      devOnly: true,
    },
    {
      title: "Logs Explorer",
      icon: "terminal",
      route: "/department/logs",
      // Support login only. ADMIN is stored under its own role name, but
      // department login treats that account as support.
      allowedRoles: ["SUPPORT", "ADMIN"],
      devOnly: true,
    },
  ];

  constructor(
    private toggleService: ToggleService,
    public themeService: CustomizerSettingsService,
    private authService: AuthService,
    private router: Router,
  ) {
    this.toggleService.isSidebarToggled$.subscribe((isSidebarToggled) => {
      this.isSidebarToggled = isSidebarToggled;
    });

    this.menuItems = this.menuItems.filter(item => this.canShowMenuItem(item));
  }

  /**
   * Whether a menu entry (top-level or sub-item) should render.
   *
   * The template used to test `visibility === 'ALL' || visibility ===
   * loggedInUser?.type` inline. But `visibility` is optional and almost no
   * sub-item declares it — Registration's children carry only
   * restrictedRoles — so for those the test became
   * `undefined === 'ALL' || undefined === <type>`. That is only ever true
   * when the user object has no `type` at all; the moment it has one, every
   * sub-item without an explicit `visibility` disappeared.
   *
   * `restrictedRoles` is a blocklist (hide for these roles, show for
   * everyone else); `visibility` and `allowedRoles` are allowlists (show
   * only for the named role or roles). An absent `visibility` means
   * "show", which is what the menu config assumes.
   */
  private canShowMenuItem(item: MenuItem): boolean {
    const userRole = this.loggedInUser?.roleName || this.loggedInUser?.type;

    // environment.production is true only for the production file replacement
    // (ng build / ng build --configuration production). Local serve and
    // --configuration development keep it false.
    if (item.devOnly && environment.production) {
      return false;
    }
    if (item.restrictedRoles?.length && userRole &&
      item.restrictedRoles.includes(userRole)) {
      return false;
    }
    if (item.allowedRoles?.length &&
      (!userRole || !item.allowedRoles.includes(userRole))) {
      return false;
    }
    if (!item.visibility || item.visibility === "ALL") {
      return true;
    }
    return item.visibility === userRole;
  }

  /** Whether a sub-menu entry should render. */
  canShowSubItem(subItem: MenuItem): boolean {
    return this.canShowMenuItem(subItem);
  }

  // Mat Expansion
  panelOpenState = false;

  isActive(item: MenuItem): boolean {
    if (item.route) {
      return this.router.isActive(item.route, {
        paths: "subset",
        queryParams: "subset",
        fragment: "ignored",
        matrixParams: "ignored",
      });
    }
    if (item.children) {
      return item.children.some((child) =>
        child.route
          ? this.router.isActive(child.route, {
            paths: "subset",
            queryParams: "subset",
            fragment: "ignored",
            matrixParams: "ignored",
          })
          : false,
      );
    }
    return false;
  }

  // toggleSidebar
  toggleSidebar() {
    this.toggleService.toggle();
  }

  logout() {
    this.authService.logout();
  }
}
